import { parseKpiSettings, serializeKpiSettings, type KpiSettingsMap } from '../audit/budgets';
import { executeListOps, type ListOp } from '../graph/listOps';
import {
  createListItem,
  deleteListItem,
  getListItem,
  getListItems,
  odataString,
  updateListItem,
  type WriteOptions,
} from '../graph/lists';
import { GraphError } from '../graph/client';
import { TEMPLATE_SCHEMA_VERSION, type TemplateDefinition } from '../audit/template';
import { validateTemplate, type TemplateSettings } from '../audit/validateTemplate';
import { migrateDraftConditions } from '../audit/templateEdit';
import { LISTS, READ_CACHE_MS } from './listNames';
import { partyOf, requirementErrors, requirementsOf, windowDays, type Party } from '../audit/requirements';
import { CLOCK_TITLE_SUFFIX, nextDueOn } from '../audit/clockCredit';
import { computeWindowStatus, daysOverdue } from '../audit/windows';
import { getNotificationRules } from './notificationConfig';
import type { Store } from './types';
import { appliesTo, isAllStores, parseAssignment, serializeAssignment, type TemplateAssignment } from '../audit/assignment';
import { getAllStores } from './stores';
import { requirementSettingsOf, toSettings, type TemplateFields } from './templates';

/**
 * Template administration (Phase 5a): every AuditTemplates row, grouped
 * into TEMPLATES ("families") by AuditType — the identity that stays the
 * same across versions, while each version is its own row with its own
 * item id (Audits.TemplateId pins the exact version an audit ran on).
 *
 * Owner decisions 2026-10-16: one live (Published) version per template;
 * publishing a new version retires the old one, and audits already started
 * or submitted on the old version keep it exactly as it was (each audit
 * carries its own template snapshot / pinned row).
 *
 * The compliance clock must survive a new version: StoreAuditStatus and
 * StoreTemplateOverrides rows reference a template ROW id, which the flows,
 * aging board and store pages match on. Publishing therefore moves those
 * rows onto the new version (finishPublish), and the sync engine credits a
 * late submission on a retired version to the live one (liveTemplateFor).
 */

export type TemplateStatus = 'Draft' | 'Published' | 'Retired';

export interface TemplateRow {
  id: number;
  name: string;
  auditType: string;
  version: number;
  status: TemplateStatus;
  /** The DM requirement's window (null = not set). */
  frequencyDays: number | null;
  dueSoonThresholdPct: number;
  /** Responsible parties: false = no DM requirement. */
  requireDm: boolean;
  /** null = no Auditor requirement. */
  auditorFrequencyDays: number | null;
  /** null = no Store team requirement (owner rulings 2026-10-19). */
  storeFrequencyDays: number | null;
  /** DM / Auditor windows for Long-distance stores; null = the normal window (owner rulings 2026-10-20). */
  longDistanceFrequencyDays: number | null;
  auditorLongDistanceFrequencyDays: number | null;
  /** false = Flow 1 sends no "due soon" email for this template (DueSoonRemindersOff). */
  dueSoonReminders: boolean;
  /** Which stores the template applies to — the same on every version (setTemplateAssignment). */
  assignment: TemplateAssignment;
  /** KPI settings (B8) — the same on every version (setTemplateKpiSettings). */
  kpiSettings?: KpiSettingsMap;
  settings: TemplateSettings;
  /** null when the stored JSON can't be read (never publishable). */
  definition: TemplateDefinition | null;
  publishedBy: string;
  publishedAt: string | null;
  /** Who last saved this row and when (server-stamped from the token, 2026-10-29); '' / null before then. */
  editedBy: string;
  editedAt: string | null;
  /** SharePoint's version stamp as read ('' if Graph sent none) — the If-Match for writes. */
  eTag: string;
}

export interface TemplateFamily {
  /** Normalized AuditType. */
  key: string;
  auditType: string;
  /** Display name: the live version's, else the newest row's. */
  name: string;
  /** Highest Published version — the one new audits use. */
  live: TemplateRow | null;
  /** All Published rows; more than one = a publish didn't finish. */
  published: TemplateRow[];
  drafts: TemplateRow[];
  retired: TemplateRow[];
  /** Every row, newest version first. */
  rows: TemplateRow[];
}

export const familyKey = (auditType: string) => auditType.trim().toLowerCase();

/**
 * Each row id → its template's CURRENT name (the newest non-draft version
 * in its family), so an audit done on v1 is still labelled "DM Facility
 * Audit" after v2 is published.
 */
export function templateNamesById(rows: readonly Pick<TemplateRow, 'id' | 'name' | 'auditType' | 'version' | 'status'>[]): Map<number, string> {
  const newest = new Map<string, { version: number; name: string }>();
  for (const r of rows) {
    if (r.status === 'Draft' || !r.name.trim()) continue;
    const k = familyKey(r.auditType);
    const cur = newest.get(k);
    if (!cur || r.version > cur.version) newest.set(k, { version: r.version, name: r.name.trim() });
  }
  const out = new Map<number, string>();
  for (const r of rows) {
    const name = newest.get(familyKey(r.auditType))?.name;
    if (name) out.set(r.id, name);
  }
  return out;
}

/** Template ids: lowercase letters, digits, '-' and '_' (same rule as question ids). */
export const AUDIT_TYPE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

function toStatus(value: unknown): TemplateStatus {
  return value === 'Published' || value === 'Retired' ? value : 'Draft';
}

export function toTemplateRow(id: string, f: Partial<TemplateFields>, eTag = ''): TemplateRow {
  let definition: TemplateDefinition | null = null;
  try {
    const parsed: unknown = f.Definition ? JSON.parse(f.Definition) : null;
    // Shape check: anything without a sections array is "unreadable".
    definition =
      parsed && typeof parsed === 'object' && Array.isArray((parsed as TemplateDefinition).sections)
        ? (parsed as TemplateDefinition)
        : null;
  } catch {
    definition = null;
  }
  return {
    id: Number(id),
    name: f.Title ?? definition?.name ?? '',
    auditType: f.AuditType ?? definition?.auditType ?? '',
    version: f.TemplateVersion ?? 1,
    status: toStatus(f.TemplateStatus),
    ...requirementSettingsOf(f),
    storeFrequencyDays: typeof f.StoreFrequencyDays === 'number' ? f.StoreFrequencyDays : null,
    longDistanceFrequencyDays: typeof f.LongDistanceFrequencyDays === 'number' ? f.LongDistanceFrequencyDays : null,
    auditorLongDistanceFrequencyDays:
      typeof f.AuditorLongDistanceFrequencyDays === 'number' ? f.AuditorLongDistanceFrequencyDays : null,
    dueSoonReminders: f.DueSoonRemindersOff !== true,
    assignment: parseAssignment(f.AssignedTo),
    kpiSettings: parseKpiSettings(f.KpiSettings),
    dueSoonThresholdPct: f.DueSoonThresholdPct ?? 80,
    settings: toSettings(f),
    definition,
    publishedBy: f.PublishedBy ?? '',
    publishedAt: f.PublishedAt ?? null,
    editedBy: (f.EditedBy ?? '').trim().toLowerCase(),
    editedAt: f.EditedAt ?? null,
    eTag,
  };
}

/** Group rows into templates, newest version first within each. */
export function groupFamilies(rows: readonly TemplateRow[]): TemplateFamily[] {
  const byKey = new Map<string, TemplateRow[]>();
  for (const r of rows) {
    const key = familyKey(r.auditType);
    byKey.set(key, [...(byKey.get(key) ?? []), r]);
  }
  return [...byKey.entries()]
    .map(([key, members]) => {
      // Version, then item id: a tie (two tabs creating the same version)
      // still resolves the same way everywhere (livePerFamily matches).
      const sorted = [...members].sort((a, b) => b.version - a.version || b.id - a.id);
      const published = sorted.filter((r) => r.status === 'Published');
      // Same rule as livePerFamily (what new audits get): a row whose
      // content can't be read is never the live version.
      const live = published.find((r) => r.definition !== null) ?? null;
      return {
        key,
        auditType: (live ?? sorted[0]!).auditType,
        name: (live ?? sorted[0]!).name,
        live,
        published,
        drafts: sorted.filter((r) => r.status === 'Draft'),
        retired: sorted.filter((r) => r.status === 'Retired'),
        rows: sorted,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** A form name compared the way people read it: spaces collapsed, any case. */
const nameKey = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * The problem with giving the form whose Template ID is `ownKey` (a new
 * form's ID too) the name `name`, or null (owner ruling 2026-10-29: no two
 * forms may share a name — the Audits list's Audit type filter, reports and
 * emails tell forms apart by it). Versions of ONE form share their name;
 * another form's current names are taken: its live and published names, its
 * draft's, and the newest named non-draft version's (what labels its audits
 * — templateNamesById). Its older retired names are free.
 */
export function formNameProblem(name: string, ownKey: string, rows: readonly TemplateRow[]): string | null {
  const wanted = nameKey(name);
  if (!wanted) return 'Give the form a name.';
  for (const f of groupFamilies(rows)) {
    if (f.key === familyKey(ownKey)) continue;
    const labelling = f.rows.find((r) => r.status !== 'Draft' && r.name.trim());
    const current = [f.name, ...f.published.map((r) => r.name), ...f.drafts.map((d) => d.name), labelling?.name ?? ''];
    const hit = current.find((n) => nameKey(n) === wanted);
    if (hit !== undefined) return `Another form (Template ID ${f.auditType.trim()}) already uses the name “${hit.trim()}” — pick a different name.`;
  }
  return null;
}

/** The version number a new draft of this template gets. */
export function nextVersion(family: TemplateFamily): number {
  return Math.max(0, ...family.rows.map((r) => r.version)) + 1;
}

/**
 * The template row a completion should be credited to: the row itself if
 * it is live, else the live version of the same template (a v1 audit that
 * syncs after v2 was published resets v2's clock). Falls back to the row
 * itself when the template has no live version.
 */
export function liveTemplateFor(
  rows: readonly TemplateRow[],
  templateId: number,
): TemplateRow | null {
  const own = rows.find((r) => r.id === templateId);
  if (!own) return null;
  const family = groupFamilies(rows).find((f) => f.key === familyKey(own.auditType));
  return family?.live ?? own;
}

/** Settings checks the definition validator doesn't cover. */
export function settingsErrors(
  row: Pick<TemplateRow, 'name' | 'frequencyDays' | 'dueSoonThresholdPct' | 'requireDm' | 'auditorFrequencyDays'> &
    Partial<Pick<TemplateRow, 'storeFrequencyDays' | 'longDistanceFrequencyDays' | 'auditorLongDistanceFrequencyDays'>>,
): string[] {
  const errors: string[] = [];
  if (!row.name.trim()) errors.push('The template needs a name.');
  errors.push(...requirementErrors(row));
  if (
    !Number.isInteger(row.dueSoonThresholdPct) ||
    row.dueSoonThresholdPct < 1 ||
    row.dueSoonThresholdPct > 99
  ) {
    errors.push('"Due soon at" must be a whole percentage between 1 and 99.');
  }
  return errors;
}

/** Everything that blocks publishing this row (empty = publishable). */
export function publishErrors(row: TemplateRow): string[] {
  const errors = settingsErrors(row);
  if (!row.definition) {
    errors.push('The template content could not be read (Definition is not valid JSON).');
    return errors;
  }
  if (familyKey(row.definition.auditType ?? '') !== familyKey(row.auditType)) {
    errors.push('The template content belongs to a different audit type.');
  }
  return [...errors, ...validateTemplate(row.definition, row.settings)];
}

// ---------------------------------------------------------------------------
// Moving the compliance clock onto a new version (pure planning)
// ---------------------------------------------------------------------------

export interface RetargetableRow {
  id: string;
  storeId: number;
  templateId: number;
  /** Compliance clocks are per store × party (responsible parties); blank = one clock per store. */
  party?: string;
  /** Tie-breaker: the row with the greatest rank survives (e.g. LastCompletedDate). */
  rank: string;
}

export interface RetargetPlan {
  /** Rows to move onto the live version. */
  retarget: string[];
  /** Duplicates for a store that already has a better row. */
  remove: string[];
}

/**
 * Per store, keep ONE row for the template: the best-ranked row among the
 * live version's and the older versions' (ties go to the row already on
 * the live version, so nothing moves without need), move it onto the live
 * version if it isn't there yet, and remove the rest. Keeping the surviving
 * row (rather than creating a new one) preserves the flows' bookkeeping
 * (LastNotifiedStatus / LastOverdueReminderAt), so nobody is re-notified.
 */
export function planRetarget(
  rows: readonly RetargetableRow[],
  oldIds: ReadonlySet<number>,
  liveId: number,
): RetargetPlan {
  const plan: RetargetPlan = { retarget: [], remove: [] };
  const byStore = new Map<string, RetargetableRow[]>();
  for (const r of rows) {
    if (r.templateId !== liveId && !oldIds.has(r.templateId)) continue;
    const key = `${r.storeId}|${r.party ?? ''}`;
    byStore.set(key, [...(byStore.get(key) ?? []), r]);
  }
  for (const group of byStore.values()) {
    const keep = group.reduce((best, r) => {
      if (r.rank > best.rank) return r;
      if (r.rank === best.rank && r.templateId === liveId && best.templateId !== liveId) return r;
      return best;
    });
    if (keep.templateId !== liveId) plan.retarget.push(keep.id);
    for (const r of group) if (r !== keep) plan.remove.push(r.id);
  }
  return plan;
}

// ---------------------------------------------------------------------------
// SharePoint operations (Admin → Templates)
// ---------------------------------------------------------------------------

/**
 * Template item id → the form's current name (any version, newest name per
 * family), for lists that name each audit's form (UI review U32/U74). Slim
 * columns and cached — no definitions.
 */
export async function getTemplateNames(): Promise<Map<number, string>> {
  const items = await getListItems<TemplateFields>(LISTS.auditTemplates, {
    select: ['Title', 'AuditType', 'TemplateVersion', 'TemplateStatus'],
    cacheMs: READ_CACHE_MS,
  });
  return templateNamesById(
    items.map((i) => ({
      id: Number(i.id),
      name: i.fields.Title ?? '',
      auditType: i.fields.AuditType ?? '',
      version: Number(i.fields.TemplateVersion) || 0,
      status: toStatus(i.fields.TemplateStatus),
    })),
  );
}

export async function getAllTemplateRows(): Promise<TemplateRow[]> {
  // Small list (a few rows per template) — fetched whole, grouped client-side.
  const items = await getListItems<TemplateFields>(LISTS.auditTemplates);
  return items.map((i) => toTemplateRow(i.id, i.fields, i.eTag));
}

export interface DraftSettings {
  name: string;
  frequencyDays: number | null;
  dueSoonThresholdPct: number;
  requireDm: boolean;
  auditorFrequencyDays: number | null;
  /** null/absent = no Store team requirement. */
  storeFrequencyDays?: number | null;
  /** Long-distance windows; null/absent = the normal window. */
  longDistanceFrequencyDays?: number | null;
  auditorLongDistanceFrequencyDays?: number | null;
  /** false = no "due soon" emails; absent = send them. */
  dueSoonReminders?: boolean;
  settings: TemplateSettings;
}

/** Row fields as written: Choice/Number columns are cleared with null. */
type WritableFields = Partial<Omit<TemplateFields, 'CriticalBehavior' | 'CriticalCap' | 'FrequencyDays' | 'StoreFrequencyDays'>> & {
  StoreFrequencyDays?: number | null;
  CriticalBehavior?: string | null;
  CriticalCap?: number | null;
  FrequencyDays?: number | null;
};

function settingsFields(s: DraftSettings): WritableFields {
  return {
    Title: s.name.trim(),
    // No DM requirement = no DM window: cleared, so a stale (or invalid)
    // number can't linger where the flows still read it.
    ...(s.requireDm
      ? s.frequencyDays !== null
        ? { FrequencyDays: s.frequencyDays }
        : {}
      : { FrequencyDays: null }),
    DueSoonThresholdPct: s.dueSoonThresholdPct,
    DmNotRequired: !s.requireDm,
    AuditorFrequencyDays: s.auditorFrequencyDays,
    StoreFrequencyDays: s.storeFrequencyDays ?? null,
    // A long-distance window only exists with its party (cleared otherwise,
    // so a stale number can't linger where the flows read it).
    LongDistanceFrequencyDays: s.requireDm ? (s.longDistanceFrequencyDays ?? null) : null,
    AuditorLongDistanceFrequencyDays: s.auditorFrequencyDays !== null ? (s.auditorLongDistanceFrequencyDays ?? null) : null,
    DueSoonRemindersOff: s.dueSoonReminders === false,
    ScoringMode: s.settings.scoringMode,
    // Only meaningful for weighted_critical; cleared otherwise so a stale
    // value can't fail validation after the mode changes.
    CriticalBehavior:
      s.settings.scoringMode === 'weighted_critical' ? (s.settings.criticalBehavior ?? null) : null,
    CriticalCap:
      s.settings.scoringMode === 'weighted_critical' && s.settings.criticalBehavior === 'cap'
        ? (s.settings.criticalCap ?? null)
        : null,
  };
}

/** POST: a new row has nothing to clear, so leave null keys out. */
function withoutNulls(f: WritableFields): WritableFields {
  return Object.fromEntries(Object.entries(f).filter(([, v]) => v !== null)) as WritableFields;
}

/**
 * PATCH: clear a Choice column with '' (the form proven in this tenant on
 * Stores.ParkingOnSite) and a Number column with null.
 */
function forPatch(f: WritableFields): WritableFields {
  return { ...f, ...(f.CriticalBehavior === null ? { CriticalBehavior: '' } : {}) };
}

/** A brand-new template: an empty Draft v1 (questions arrive in Phase 5b). */
export async function createTemplate(
  auditType: string,
  s: DraftSettings,
  existing: readonly TemplateRow[],
): Promise<void> {
  const type = auditType.trim().toLowerCase();
  if (!AUDIT_TYPE_PATTERN.test(type)) {
    throw new Error('The template ID may use only lowercase letters, numbers, "-" and "_".');
  }
  // Re-read: another tab or admin may have added a form since this page loaded.
  const fresh = await getAllTemplateRows().catch(() => [...existing]);
  if ([...existing, ...fresh].some((r) => familyKey(r.auditType) === type)) {
    throw new Error(`A template with the ID "${type}" already exists.`);
  }
  const nameProblem = formNameProblem(s.name, type, fresh);
  if (nameProblem) throw new Error(nameProblem);
  const definition: TemplateDefinition = {
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    name: s.name.trim(),
    auditType: type,
    sections: [],
  };
  await createListItem<WritableFields>(LISTS.auditTemplates, {
    ...withoutNulls(settingsFields(s)),
    AuditType: type,
    TemplateVersion: 1,
    TemplateStatus: 'Draft',
    Definition: JSON.stringify(definition),
  });
}

/**
 * A new Draft version copied from an existing version (any status). Re-reads
 * the template first, so a stale page (or a second tab) can't create a
 * second draft or reuse a version number.
 */
export async function createDraftFrom(source: TemplateRow): Promise<void> {
  const family = groupFamilies(await getAllTemplateRows()).find(
    (f) => f.key === familyKey(source.auditType),
  );
  if (!family) throw new Error('That template no longer exists — reload the page.');
  if (family.drafts.length > 0) {
    throw new Error('This template already has a draft — edit or delete that one first.');
  }
  if (!source.definition) {
    throw new Error('That version’s content can’t be read, so it can’t be copied.');
  }
  await createListItem<WritableFields>(LISTS.auditTemplates, {
    ...withoutNulls(settingsFields({
      name: source.name,
      frequencyDays: source.frequencyDays,
      dueSoonThresholdPct: source.dueSoonThresholdPct,
      requireDm: source.requireDm,
      auditorFrequencyDays: source.auditorFrequencyDays,
      storeFrequencyDays: source.storeFrequencyDays,
      longDistanceFrequencyDays: source.longDistanceFrequencyDays,
      auditorLongDistanceFrequencyDays: source.auditorLongDistanceFrequencyDays,
      dueSoonReminders: source.dueSoonReminders,
      settings: source.settings,
    })),
    ...(serializeAssignment(source.assignment) ? { AssignedTo: serializeAssignment(source.assignment) } : {}),
    ...(source.kpiSettings && Object.keys(source.kpiSettings.q).length > 0 ? { KpiSettings: serializeKpiSettings(source.kpiSettings) } : {}),
    AuditType: source.auditType,
    TemplateVersion: nextVersion(family),
    TemplateStatus: 'Draft',
    // Legacy "Show this question" conditions become Show rules (owner ruling 2026-11-02).
    Definition: JSON.stringify(migrateDraftConditions(source.definition)),
  });
}

/**
 * Which stores a template applies to (owner rulings 2026-10-19): written to
 * EVERY version row of the family, so a draft, the live version and retired
 * ones never disagree (readers use the live row; a new draft copies it).
 * '' = all stores. Re-reads the family first (a draft created in another
 * tab gets it too).
 */
export async function setTemplateAssignment(auditType: string, assignment: TemplateAssignment): Promise<void> {
  const family = groupFamilies(await getAllTemplateRows()).find((f) => f.key === familyKey(auditType));
  if (!family) throw new Error('That template no longer exists — reload the page.');
  const value = serializeAssignment(assignment);
  // Judge "taken off" against EXACTLY what is stored (lower case, trimmed). The dialog hands back
  // districts as the stores spell them ("[SIM] District 1000"); compared raw with the stores'
  // lower-cased districts, every store of a picked district looked taken off and lost its due
  // dates (owner's DM Visit, 2026-10-27 1:35 PM — found 2026-10-29).
  const saved = parseAssignment(value);
  const responses = await executeListOps(
    family.rows.map((r, i) => ({
      id: `a-${i}`,
      method: 'PATCH' as const,
      list: LISTS.auditTemplates,
      itemId: String(r.id),
      fields: { AssignedTo: value },
    })),
  );
  const failed = [...responses.values()].filter((r) => r.status < 200 || r.status >= 300).length;
  if (failed > 0) {
    throw new Error(`${failed} of ${family.rows.length} versions weren’t updated — save the assignment again.`);
  }
  // Stores taken off lose their clocks (QA review 2026-10-19): kept, they'd
  // come back long overdue if the store were added again — a burst of
  // overdue/escalation mail on the next Flow 1 run, the same risk publishing
  // avoids for dropped parties. Re-added stores start fresh at their next
  // audit (no nagging before it). Their audits stay on record.
  if (!isAllStores(saved)) {
    const stores = await getAllStores({ includeInactive: true });
    const byId = new Map(stores.map((s) => [Number(s.id), s]));
    const clocks = await rowsForTemplates<StatusFields>(LISTS.storeAuditStatus, family.rows.map((r) => r.id));
    const off = clocks.filter((c) => {
      const store = byId.get(Number(c.fields.StoreId));
      return store !== undefined && !appliesTo(saved, store);
    });
    const deleted = await executeListOps(
      off.map((c, i) => ({ id: `c-${i}`, method: 'DELETE' as const, list: LISTS.storeAuditStatus, itemId: c.id })),
    );
    const notDeleted = [...deleted.values()].filter((r) => (r.status < 200 || r.status >= 300) && r.status !== 404).length;
    if (notDeleted > 0) {
      throw new Error(`Saved, but ${notDeleted} due dates of stores taken off weren’t cleared — save again to retry.`);
    }
  }
  // A store put (back) on the form keeps its history (owner ruling 2026-10-29, replacing
  // "starts fresh at its next audit"): its due date is rebuilt from its last audit, quietly.
  await rebuildMissingClocks(auditType);
}

/** A submitted audit as the rebuild reads it. */
export interface HistoryAudit {
  storeId: number;
  submittedAt: string;
  /** Audits.Party as stored ('auditor' | 'store'; blank = DM). */
  party: unknown;
}

/**
 * The due-date rows a form is MISSING, rebuilt from its audit history (owner
 * rulings 2026-10-29): for every active store the live version applies to,
 * each required party (DM / Auditor / Store team) with NO row on any version
 * of the form but at least one submitted audit gets a row from its latest
 * audit — exactly what that audit's credit would have written (Eastern day
 * + window, the long-distance window too). Never-audited stores get none
 * (the first audit starts the clock). Flow 1 is told the store was already
 * told — LastNotifiedStatus = the status now and, when overdue,
 * LastOverdueReminderAt = now — so no backlog of "due" or "overdue" mail goes
 * out and reminders resume on their cadence. 'escalated' only once the row is
 * already past the escalation point (`escalationAfterDays`, Flow 1's rule):
 * a store 5 days overdue still escalates on day 14 (QA 2026-10-29). An audit
 * with an unreadable or future SubmittedAt is skipped — the conductor writes
 * it, and the server's credit doesn't trust such a date either (security
 * 2026-10-29).
 * Which audits count for a party mirrors the clocks: a form with one staff
 * requirement credits every staff audit to it; with both, Audits.Party
 * (server-stamped) decides; store-team audits only ever count for the store.
 * Pure — tested.
 */
export function planMissingClocks(input: {
  live: TemplateRow;
  stores: readonly Store[];
  /** Existing rows on ANY version of the form. */
  existing: readonly { storeId: number; party: unknown }[];
  audits: readonly HistoryAudit[];
  now: Date;
  /** Flow 1's audit_escalation rule: days overdue before it escalates (default 14). */
  escalationAfterDays?: number;
}): Record<string, unknown>[] {
  const escalateAt = input.escalationAfterDays && input.escalationAfterDays > 0 ? input.escalationAfterDays : 14;
  const nowMs = input.now.getTime();
  const usable = (a: HistoryAudit) => {
    const t = Date.parse(a.submittedAt);
    return Number.isFinite(t) && t <= nowMs;
  };
  const reqs = requirementsOf(input.live);
  const staffReqs = reqs.filter((r) => r.party !== 'store').length;
  const has = new Set(input.existing.map((r) => `${Number(r.storeId)}|${partyOf(r.party)}`));
  const counts = (a: HistoryAudit, party: Party) => {
    const p = partyOf(a.party);
    if (party === 'store') return p === 'store';
    return p !== 'store' && (staffReqs === 1 || p === party);
  };
  const out: Record<string, unknown>[] = [];
  for (const store of input.stores) {
    if (!store.active || !appliesTo(input.live.assignment, store)) continue;
    const storeId = Number(store.id);
    for (const req of reqs) {
      if (has.has(`${storeId}|${req.party}`)) continue;
      const last = input.audits
        .filter((a) => Number(a.storeId) === storeId && usable(a) && counts(a, req.party))
        .sort((a, b) => Date.parse(b.submittedAt) - Date.parse(a.submittedAt))[0];
      if (!last) continue;
      const days = windowDays(req, store.longDistance === true);
      const nextDue = nextDueOn(last.submittedAt, days);
      const status = computeWindowStatus(
        { lastCompleted: last.submittedAt, nextDue, frequencyDays: days, dueSoonThresholdPct: input.live.dueSoonThresholdPct },
        input.now,
      );
      out.push({
        Title: `${store.storeNumber} — ${input.live.name}${CLOCK_TITLE_SUFFIX[req.party]}`,
        StoreId: storeId,
        TemplateId: input.live.id,
        LastCompletedDate: last.submittedAt,
        NextDueDate: nextDue,
        WindowStatus: status,
        LastNotifiedStatus: status === 'overdue' && daysOverdue(nextDue, input.now) >= escalateAt ? 'escalated' : status,
        ...(status === 'overdue' ? { LastOverdueReminderAt: input.now.toISOString() } : {}),
        ...(req.party !== 'dm' ? { Party: req.party } : {}),
      });
    }
  }
  return out;
}

interface HistoryFields {
  StoreId: number;
  SubmittedAt: string | null;
  Party?: string | null;
}

/**
 * Admin → Templates "Rebuild missing due dates" (and every assignment save):
 * creates the rows planMissingClocks finds. Returns how many were created.
 */
export async function rebuildMissingClocks(auditType: string, now: Date = new Date()): Promise<{ created: number; onOldVersion: number }> {
  const family = groupFamilies(await getAllTemplateRows()).find((f) => f.key === familyKey(auditType));
  const live = family?.live;
  if (!family || !live) return { created: 0, onOldVersion: 0 };
  const ids = family.rows.map((r) => r.id);
  const [stores, existing, audits, rules] = await Promise.all([
    getAllStores(),
    rowsForTemplates<StatusFields>(LISTS.storeAuditStatus, ids),
    Promise.all(
      ids.map((id) =>
        getListItems<HistoryFields>(LISTS.audits, {
          filter: `fields/TemplateId eq ${id} and fields/AuditStatus eq 'submitted'`,
          select: ['StoreId', 'SubmittedAt', 'Party'],
        }),
      ),
    ).then((pages) => pages.flat()),
    getNotificationRules().catch(() => []),
  ]);
  // Flow 1 reads the first enabled audit_escalation rule.
  const escalation = rules.find((r) => r.eventType === 'audit_escalation' && r.enabled);
  const rows = planMissingClocks({
    live,
    stores,
    existing: existing.map((r) => ({ storeId: Number(r.fields.StoreId), party: r.fields.Party })),
    audits: audits.map((a) => ({ storeId: Number(a.fields.StoreId), submittedAt: a.fields.SubmittedAt ?? '', party: a.fields.Party })),
    now,
    ...(escalation?.escalationAfterDays ? { escalationAfterDays: escalation.escalationAfterDays } : {}),
  });
  // Rows still on an older version count as present but show as "never" until moved (Re-check due dates).
  const onOldVersion = existing.filter((r) => Number(r.fields.TemplateId) !== live.id).length;
  if (rows.length === 0) return { created: 0, onOldVersion };
  const responses = await executeListOps(rows.map((fields, i) => ({ id: `n-${i}`, method: 'POST' as const, list: LISTS.storeAuditStatus, fields })));
  const failed = [...responses.values()].filter((r) => r.status < 200 || r.status >= 300).length;
  if (failed > 0) throw new Error(`${failed} of ${rows.length} due dates weren’t created — run “Rebuild missing due dates” again.`);
  return { created: rows.length, onOldVersion };
}

/**
 * KPI settings (B8, owner rulings 2026-10-25) — written to EVERY version of
 * the template, like the assignment, so a new version keeps them. Admin only
 * (the server refuses other roles' template writes).
 */
export async function setTemplateKpiSettings(auditType: string, settings: KpiSettingsMap): Promise<void> {
  const family = groupFamilies(await getAllTemplateRows()).find((f) => f.key === familyKey(auditType));
  if (!family) throw new Error('That template no longer exists — reload the page.');
  const value = serializeKpiSettings(settings);
  const responses = await executeListOps(
    family.rows.map((r, i) => ({ id: `k-${i}`, method: 'PATCH' as const, list: LISTS.auditTemplates, itemId: String(r.id), fields: { KpiSettings: value } })),
  );
  const failed = [...responses.values()].filter((r) => r.status < 200 || r.status >= 300).length;
  if (failed > 0) throw new Error(`${failed} of ${family.rows.length} versions weren’t updated — save the KPI settings again.`);
}

/** One template row by item id (any status); null if it's gone. */
export async function getTemplateRow(id: number): Promise<TemplateRow | null> {
  const item = await getListItem<TemplateFields>(LISTS.auditTemplates, id);
  return item ? toTemplateRow(item.id, item.fields, item.eTag) : null;
}

/**
 * The draft was changed somewhere else (another tab, another admin, or a
 * direct SharePoint edit) since this copy was read. Nothing was written.
 */
/** A StaleDraftError naming who saved `now` (the row as it stands) last; null = not known. */
function staleFrom(now: TemplateRow | null): StaleDraftError {
  const stale = new StaleDraftError();
  if (now) {
    stale.editedBy = now.editedBy;
    stale.editedAt = now.editedAt;
  }
  return stale;
}

export class StaleDraftError extends Error {
  /**
   * Who saved the draft last, and when, as read right after the clash (owner
   * ask 2026-10-29: "so they can check in with that person"); '' / null when
   * not recorded or the re-read failed.
   */
  editedBy = '';
  editedAt: string | null = null;
  constructor() {
    super('This draft has been edited somewhere else (another tab or admin) since you opened it here. Nothing was saved — Cancel, reload the page and make your change again.');
    this.name = 'StaleDraftError';
  }
}

const isPreconditionFailed = (err: unknown) => err instanceof GraphError && err.status === 412;

/**
 * A write guarded by If-Match on the eTag `row` was read with, so the
 * read-check-write (still a Draft? still the content we validated?) is
 * atomic: anything that changed the row in between makes SharePoint refuse
 * it (412) instead of overwriting. Rows read without an eTag write unguarded.
 */
async function guarded(row: TemplateRow, write: (opts: WriteOptions) => Promise<void>): Promise<void> {
  try {
    await write(row.eTag ? { ifMatch: row.eTag } : {});
  } catch (err) {
    if (!isPreconditionFailed(err)) throw err;
    throw staleFrom(await getTemplateRow(row.id).catch(() => null));
  }
}

/** Re-read one row and insist it is still a Draft before changing it. */
async function mustBeDraft(id: number): Promise<TemplateRow> {
  const item = await getListItem<TemplateFields>(LISTS.auditTemplates, id);
  if (!item) throw new Error('That draft no longer exists — reload the page.');
  const row = toTemplateRow(item.id, item.fields, item.eTag);
  if (row.status !== 'Draft') {
    throw new Error(`Version ${row.version} is ${row.status.toLowerCase()} and can no longer be changed.`);
  }
  return row;
}

export async function updateDraftSettings(id: number, s: DraftSettings): Promise<void> {
  const row = await mustBeDraft(id);
  const nameProblem = formNameProblem(s.name, row.auditType, await getAllTemplateRows());
  if (nameProblem) throw new Error(nameProblem);
  const definition = row.definition ? { ...row.definition, name: s.name.trim() } : null;
  await guarded(row, (opts) =>
    updateListItem<WritableFields>(
      LISTS.auditTemplates,
      String(id),
      {
        ...forPatch(settingsFields(s)),
        ...(definition ? { Definition: JSON.stringify(definition) } : {}),
      },
      opts,
    ),
  );
}

/**
 * Save a draft's questions (Phase 5b). The template's name and Template ID
 * always come from the row, so the editor can't move content between
 * templates. Validation is NOT enforced here — drafts may be saved half-done;
 * publishing is where errors block.
 *
 * `expectedETag` = the eTag the editor opened (or last saved) the draft at:
 * if the row changed since, StaleDraftError and nothing is written (5c — two
 * tabs or admins can't silently overwrite each other). Omit it to save over
 * whatever is there now ("save mine anyway"), still only onto a Draft.
 * Returns the row's new eTag for the next save.
 */
export async function updateDraftDefinition(
  id: number,
  definition: TemplateDefinition,
  expectedETag?: string,
): Promise<string> {
  const row = await mustBeDraft(id);
  // '' = the caller doesn't know its eTag (e.g. the re-read below failed):
  // treat like "save anyway" but still guarded on this fresh read.
  if (expectedETag && row.eTag && expectedETag !== row.eTag) throw staleFrom(row);
  const next: TemplateDefinition = {
    ...definition,
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    name: row.name,
    auditType: row.auditType,
  };
  const guardRow = expectedETag ? { ...row, eTag: expectedETag } : row;
  await guarded(guardRow, (opts) =>
    updateListItem<WritableFields>(LISTS.auditTemplates, String(id), { Definition: JSON.stringify(next) }, opts),
  );
  // The PATCH response's own etag field isn't documented as the item eTag;
  // re-read for the one getListItem returns (same source as every compare).
  // The save already landed, so a failed re-read must not report failure.
  try {
    return (await getListItem<TemplateFields>(LISTS.auditTemplates, id))?.eTag ?? '';
  } catch {
    return '';
  }
}

export async function deleteDraft(id: number): Promise<void> {
  const row = await mustBeDraft(id);
  await guarded(row, (opts) => deleteListItem(LISTS.auditTemplates, String(id), opts));
}

/**
 * Publish a Draft: validate, mark it Published, then finishPublish moves
 * the compliance clock across and retires older versions. Safe to re-run:
 * finishPublish alone completes a publish that stopped half-way.
 */
export async function publishDraft(id: number, publisherUpn: string): Promise<void> {
  const row = await mustBeDraft(id);
  const errors = publishErrors(row);
  // A draft named before the rule (2026-10-29) may still clash with another form.
  const nameProblem = formNameProblem(row.name, row.auditType, await getAllTemplateRows());
  if (nameProblem) errors.push(nameProblem);
  if (errors.length > 0) throw new Error(`Can’t publish yet: ${errors.join(' ')}`);
  // If-Match: a save landing between the validation above and this write
  // would otherwise put unvalidated content on the Published row.
  try {
    await guarded(row, (opts) =>
      updateListItem<WritableFields>(
        LISTS.auditTemplates,
        String(id),
        { TemplateStatus: 'Published', PublishedBy: publisherUpn, PublishedAt: new Date().toISOString() },
        opts,
      ),
    );
  } catch (err) {
    // A 5xx retry of a write that actually landed comes back 412: if the row
    // is now Published by us, that was our own write — carry on.
    const now = err instanceof StaleDraftError ? await getTemplateRow(id) : null;
    if (!(now?.status === 'Published' && now.publishedBy === publisherUpn)) throw err;
  }
  await finishPublish(row.auditType);
}

interface StatusFields {
  Title: string;
  StoreId: number;
  TemplateId: number;
  LastCompletedDate: string | null;
  Party?: string | null;
}

interface OverrideFields {
  StoreId: number;
  TemplateId: number;
  Active: boolean | null;
}

/**
 * Rows of one list whose TemplateId is any of `ids` — one indexed `eq`
 * query per id (Graph documents no `or` for list-item filters, and a failed
 * read here would leave a publish stuck half-way).
 */
async function rowsForTemplates<T>(list: string, ids: readonly number[]) {
  const pages = await Promise.all(
    ids.map((id) => getListItems<T>(list, { filter: `fields/TemplateId eq ${id}` })),
  );
  return pages.flat();
}

async function runOrThrow(requests: ListOp[], what: string): Promise<void> {
  if (requests.length === 0) return;
  const responses = await executeListOps(requests);
  const failed = [...responses.values()].filter((r) => r.status < 200 || r.status >= 300);
  if (failed.length > 0) {
    throw new Error(
      `${failed.length} of ${requests.length} ${what} updates failed — click “Finish publishing” to retry.`,
    );
  }
}

/**
 * Bring a template to a consistent published state around its live
 * (highest Published) version:
 * 1. move StoreAuditStatus rows from older versions onto it (one per store),
 * 2. the same for StoreTemplateOverrides (per-store frequency overrides),
 * 3. retire every other Published version.
 * Order matters: the clock is moved BEFORE the old version is retired, so a
 * failure part-way leaves two Published versions (the banner's cue to
 * retry) rather than stores silently showing "never audited".
 */
export async function finishPublish(auditType: string): Promise<void> {
  const family = groupFamilies(await getAllTemplateRows()).find(
    (f) => f.key === familyKey(auditType),
  );
  const live = family?.live;
  if (!family || !live) return;
  const oldIds = family.rows.filter((r) => r.id !== live.id).map((r) => r.id);

  if (oldIds.length > 0) {
    const allIds = [live.id, ...oldIds];

    const allStatusItems = await rowsForTemplates<StatusFields>(LISTS.storeAuditStatus, allIds);
    // A party the live version no longer requires: its clocks are removed
    // (Audits keep the history). Keeping them would mean re-adding that
    // party later finds every store long overdue → a burst of overdue and
    // escalation mail on the next Flow 1 run (QA review 2026-10-18).
    // Only trusted when the live version's settings are VALID: a live row
    // edited by hand in SharePoint (blank or decimal frequency) would
    // otherwise parse as "nobody required" and "Re-check due
    // dates" would delete every clock. Invalid → keep everything.
    const liveRequirements = requirementErrors(live).length === 0 ? requirementsOf(live) : [];
    const required = new Set(liveRequirements.map((r) => r.party));
    const keepAll = liveRequirements.length === 0;
    const dropped = keepAll ? [] : allStatusItems.filter((i) => !required.has(partyOf(i.fields.Party)));
    const statusItems = keepAll
      ? allStatusItems
      : allStatusItems.filter((i) => required.has(partyOf(i.fields.Party)));
    const statusPlan = planRetarget(
      statusItems.map((i) => ({
        id: i.id,
        storeId: i.fields.StoreId,
        templateId: i.fields.TemplateId,
        party: partyOf(i.fields.Party),
        rank: i.fields.LastCompletedDate ?? '',
      })),
      new Set(oldIds),
      live.id,
    );
    const titleFor = (id: string) => {
      const item = statusItems.find((i) => i.id === id);
      const title = item?.fields.Title ?? '';
      const store = title.split(' — ')[0];
      const suffix = CLOCK_TITLE_SUFFIX[partyOf(item?.fields.Party)];
      return store ? `${store} — ${live.name}${suffix}` : title;
    };
    await runOrThrow(
      [
        ...statusPlan.retarget.map((id, i) => ({
          id: `s-${i}`,
          method: 'PATCH' as const,
          list: LISTS.storeAuditStatus,
          itemId: id,
          fields: { TemplateId: live.id, Title: titleFor(id) },
        })),
        ...[...statusPlan.remove, ...dropped.map((i) => i.id)].map((id, i) => ({
          id: `sd-${i}`,
          method: 'DELETE' as const,
          list: LISTS.storeAuditStatus,
          itemId: id,
        })),
      ],
      'compliance-clock',
    );

    const overrideItems = await rowsForTemplates<OverrideFields>(LISTS.storeTemplateOverrides, allIds);
    const overridePlan = planRetarget(
      // Several rows for a store: keep an ACTIVE one if any (an inactive
      // row is kept configuration, not the one in force), newest first.
      overrideItems.map((i) => ({
        id: i.id,
        storeId: i.fields.StoreId,
        templateId: i.fields.TemplateId,
        rank: `${i.fields.Active === false ? '0' : '1'}${i.id.padStart(12, '0')}`,
      })),
      new Set(oldIds),
      live.id,
    );
    await runOrThrow(
      [
        ...overridePlan.retarget.map((id, i) => ({
          id: `o-${i}`,
          method: 'PATCH' as const,
          list: LISTS.storeTemplateOverrides,
          itemId: id,
          fields: { TemplateId: live.id },
        })),
        ...overridePlan.remove.map((id, i) => ({
          id: `od-${i}`,
          method: 'DELETE' as const,
          list: LISTS.storeTemplateOverrides,
          itemId: id,
        })),
      ],
      'per-store frequency',
    );
  }

  await runOrThrow(
    family.published
      .filter((r) => r.id !== live.id)
      .map((r, i) => ({
        id: `r-${i}`,
        method: 'PATCH' as const,
        list: LISTS.auditTemplates,
        itemId: String(r.id),
        fields: { TemplateStatus: 'Retired' },
      })),
    'retire-old-version',
  );
}

/**
 * Sync-engine helper: the live version a completion on `templateId` should
 * be credited to (see liveTemplateFor). Reads the audit's own row plus the
 * Published rows (TemplateStatus is indexed) — not every draft. A READ
 * FAILURE THROWS on purpose: the engine retries the whole header step,
 * whereas guessing the audit's own (possibly retired) version would credit
 * the completion to a row nothing reads, leaving the store looking overdue.
 * Returns null only when the audit's own row no longer exists.
 */
export async function resolveLiveTemplate(templateId: number): Promise<TemplateRow | null> {
  const own = await getListItem<TemplateFields>(LISTS.auditTemplates, templateId);
  if (!own) return null;
  const published = await getListItems<TemplateFields>(LISTS.auditTemplates, {
    filter: `fields/TemplateStatus eq ${odataString('Published')}`,
  });
  const rows = [own, ...published.filter((p) => p.id !== own.id)].map((i) =>
    toTemplateRow(i.id, i.fields, i.eTag),
  );
  return liveTemplateFor(rows, templateId);
}
